import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";

/** ---------------------- Tipos ---------------------- */

type PerdasConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;

  departamento: string;
  awaitDepartamento: string;

  title: string;
  reportTitle: string;
  fileNamePrefix: string;
};

export type PerdaItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type RetornoSolicitacaoAPI = {
  id: number | null;
  status: "solicitado" | "aprovado" | "negado" | null;
  quantidade: number | null;
  motivo: string | null;
  observacao: string | null;
  motivo_negativa: string | null;
  criado_em: string | null;
  analisado_em: string | null;
};

export type PerdaMovAPI = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    criado_em: string;
    motivo: string | null;
    tipo?: string | null;
    tipo_full?: string | null;
    retorno_solicitacao?: RetornoSolicitacaoAPI | null;
  };
  itens: PerdaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC";
      limit: number;
      offset: number;
    };
    count: number;
  };
  data: PerdaMovAPI[];
};

export type PerdaRowUI = [
  string, // Responsável
  string, // Código
  string, // Tipo
  string, // Nome do Produto
  string, // Custo Unitário
  string, // Quantidade
  string, // Motivo
  string, // Data
  string // Hora
];

export type RetornoStatusUI = "none" | "solicitado" | "aprovado" | "negado";

export type PerdaRowMeta = {
  movimentacaoId: number;
  produtoId: number;
  itemId: number;

  responsavelId: number | null;
  responsavelNome: string | null;

  codigo: string;
  nomeProduto: string;

  tipo: string;
  tipoFull: string;

  quantidade: number;
  custoUnitario: number;
  motivo: string | null;

  retornoSolicitacaoId: number | null;
  retornoStatus: RetornoStatusUI;
  retornoQuantidade: number | null;
  retornoMotivo: string | null;
  retornoObservacao: string | null;
  retornoMotivoNegativa: string | null;
  retornoCriadoEm: string | null;
  retornoAnalisadoEm: string | null;
};

export type PerdasQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

type AwaitResult = {
  changed: boolean;
  versao: number | null;
};

type Row = (string | number | React.ReactNode)[];

type ItemPerdaModal = {
  originalIndex: number;
  row: Row;
  meta: PerdaRowMeta | null;
};

type ModalState = {
  open: boolean;
  responsavel: string;
  motivo: string;
  data: string;
  hora: string;
  itens: ItemPerdaModal[];
};

/** ---------------------- Constantes ---------------------- */

const API_BASE = "https://api.eva-googlemarine.com";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Configs antigas + fallback dinâmico ---------------------- */

const PERDAS_CONFIGS: PerdasConfig[] = [
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-e-comercial",
    key: "suprimentos-servico-comercial",
    departamento: "suprimentos",
    awaitDepartamento: "suprimentos",
    title: "Suprimentos - Perdas do Estoque de Serviço & Comercial",
    reportTitle: "Relatório de Perdas - Suprimentos",
    fileNamePrefix: "Suprimentos-Perdas",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-campos",
    key: "suprimentos-campos",
    departamento: "suprimentos-campos",
    awaitDepartamento: "suprimentos-campos",
    title: "Suprimentos Campos - Perdas",
    reportTitle: "Relatório de Perdas - Suprimentos Campos",
    fileNamePrefix: "Suprimentos-Campos-Perdas",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-tecnicos-e-laboratorio",
    key: "tecnicos",
    departamento: "tecnicos",
    awaitDepartamento: "tecnicos",
    title: "Laboratório - Saídas como Perdas",
    reportTitle: "Relatório de Perdas - Laboratório",
    fileNamePrefix: "Laboratorio-Perdas",
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-uso-coorporativo",
    key: "recursos-humanos",
    departamento: "recursos-humanos",
    awaitDepartamento: "recursos-humanos",
    title: "Suprimentos - Estoque p/ Uso Coorporativo - Saídas como Perdas",
    reportTitle: "Relatório de Perdas - Estoque p/ Uso Coorporativo",
    fileNamePrefix: "Uso-Coorporativo-Perdas",
  },
];

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): PerdasConfig {
  const area = normalizeSlug(areaSlug);
  const estoque = normalizeSlug(estoqueSlug);

  const found = PERDAS_CONFIGS.find(
    (config) => config.areaSlug === area && config.estoqueSlug === estoque
  );

  if (found) return found;

  const safeArea = area || "suprimentos";
  const safeEstoque = estoque || "estoque";
  const estoqueTitle = slugToTitle(safeEstoque);

  return {
    areaSlug: safeArea,
    estoqueSlug: safeEstoque,
    key: `${safeArea}-${safeEstoque}`,
    departamento: safeEstoque,
    awaitDepartamento: safeEstoque,
    title: `${slugToTitle(safeArea)} - Perdas - ${estoqueTitle}`,
    reportTitle: `Relatório de Perdas - ${estoqueTitle}`,
    fileNamePrefix: `${safeArea}-${safeEstoque}-Perdas`,
  };
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  const d = new Date(iso);

  if (!Number.isNaN(d.getTime())) {
    return {
      data: d.toLocaleDateString("pt-BR", {
        timeZone: "America/Sao_Paulo",
      }),
      hora: d.toLocaleTimeString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  }

  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );

  if (!m) {
    return {
      data: iso,
      hora: "",
    };
  }

  const [, ano, mes, dia, hora, minuto] = m;

  return {
    data: `${dia}/${mes}/${ano}`,
    hora: `${hora}:${minuto}`,
  };
}

function formatBRL(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseQuantidade(value: unknown): number {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function parseBRLValue(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const raw = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .trim();

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma > lastDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = raw.replace(/,/g, "");
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function normalizeRetornoStatus(
  status: RetornoSolicitacaoAPI["status"] | undefined | null
): RetornoStatusUI {
  if (status === "solicitado") return "solicitado";
  if (status === "aprovado") return "aprovado";
  if (status === "negado") return "negado";
  return "none";
}

function flattenToRows(movs: PerdaMovAPI[]): {
  rows: PerdaRowUI[];
  rowMeta: PerdaRowMeta[];
} {
  const rows: PerdaRowUI[] = [];
  const rowMeta: PerdaRowMeta[] = [];

  for (const mov of movs || []) {
    const { data, hora } = toBRT(mov.movimentacao.criado_em);

    const responsavel = mov.movimentacao.responsavel_nome?.trim() || "-";
    const motivo = mov.movimentacao.motivo?.trim() || "-";
    const tipo = mov.movimentacao.tipo?.trim() || "perda";
    const tipoFull = mov.movimentacao.tipo_full?.trim() || "perda";

    const retorno = mov.movimentacao.retorno_solicitacao || null;
    const retornoStatus = normalizeRetornoStatus(retorno?.status);

    for (const item of mov.itens || []) {
      const quantidade = Number(item.quantidade || 0);
      const custo = Number(item.custo_unitario ?? "0");

      const codigo = String(item.produto?.codigo ?? "").toUpperCase();
      const nomeProduto = String(item.produto?.nome ?? "").toUpperCase();

      rows.push([
        responsavel,
        codigo,
        tipo,
        nomeProduto,
        `R$ ${formatBRL(Number.isFinite(custo) ? custo : 0)}`,
        String(Number.isFinite(quantidade) ? quantidade : 0),
        motivo,
        data,
        hora,
      ]);

      rowMeta.push({
        movimentacaoId: mov.movimentacao.id,
        produtoId: item.produto.id,
        itemId: item.id,

        responsavelId: mov.movimentacao.responsavel_id,
        responsavelNome: mov.movimentacao.responsavel_nome,

        codigo,
        nomeProduto,

        tipo,
        tipoFull,

        quantidade: Number.isFinite(quantidade) ? quantidade : 0,
        custoUnitario: Number.isFinite(custo) ? custo : 0,
        motivo: mov.movimentacao.motivo ?? null,

        retornoSolicitacaoId: retorno?.id ?? null,
        retornoStatus,
        retornoQuantidade: retorno?.quantidade ?? null,
        retornoMotivo: retorno?.motivo ?? null,
        retornoObservacao: retorno?.observacao ?? null,
        retornoMotivoNegativa: retorno?.motivo_negativa ?? null,
        retornoCriadoEm: retorno?.criado_em ?? null,
        retornoAnalisadoEm: retorno?.analisado_em ?? null,
      });
    }
  }

  return {
    rows,
    rowMeta,
  };
}

function buildQuery(config: PerdasConfig, query?: PerdasQuery): string {
  const params = new URLSearchParams();

  params.set("estoque_slug", config.estoqueSlug);

  if (query?.date_from) params.set("date_from", query.date_from);
  if (query?.date_to) params.set("date_to", query.date_to);
  if (query?.order) params.set("order", query.order);
  if (typeof query?.limit === "number")
    params.set("limit", String(query.limit));
  if (typeof query?.offset === "number")
    params.set("offset", String(query.offset));

  const s = params.toString();

  return s ? `?${s}` : "";
}

function getPerdasUrl(config: PerdasConfig): string {
  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.departamento
  )}/perdas`;
}

function getAwaitUrl(config: PerdasConfig, since: number): string {
  const params = new URLSearchParams();

  params.set("since", String(since));
  params.set("timeout_ms", String(AWAIT_TIMEOUT_MS));
  params.set("estoque_slug", config.estoqueSlug);

  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.awaitDepartamento
  )}/perdas/await?${params.toString()}`;
}

/** ---------------------- Fetchers ---------------------- */

async function fetchPerdasOnce(
  config: PerdasConfig,
  query?: PerdasQuery
): Promise<{
  rows: PerdaRowUI[];
  rowMeta: PerdaRowMeta[];
  meta: RespAPI["meta"] | null;
}> {
  const res = await fetch(
    `${getPerdasUrl(config)}${buildQuery(config, query)}`,
    {
      method: "GET",
      headers: buildAuthHeaders(),
      cache: "no-store",
    }
  );

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const json = (await res.json()) as RespAPI;

  if (!json.ok) {
    return {
      rows: [],
      rowMeta: [],
      meta: json.meta ?? null,
    };
  }

  const flat = flattenToRows(Array.isArray(json.data) ? json.data : []);

  return {
    rows: flat.rows,
    rowMeta: flat.rowMeta,
    meta: json.meta ?? null,
  };
}

async function awaitChange(
  config: PerdasConfig,
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const res = await fetch(getAwaitUrl(config, since), {
      method: "GET",
      headers: buildAuthHeaders(),
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();

    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;

    const changed = Boolean(json?.changed);

    return {
      changed,
      versao,
    };
  } catch {
    return null;
  }
}

/** ---------------------- Hook genérico ---------------------- */

function usePerdasGenerico(config: PerdasConfig, query?: PerdasQuery) {
  const [rows, setRows] = useState<PerdaRowUI[]>([]);
  const [rowMeta, setRowMeta] = useState<PerdaRowMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchPerdasOnce(config, query);

      setRows(result.rows);
      setRowMeta(result.rowMeta);
      setMeta(result.meta);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar perdas.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    setLoading(true);
    setErro(null);
    setRows([]);
    setRowMeta([]);
    setMeta(null);

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const initResp = await awaitChange(config, -1);

        if (initResp && typeof initResp.versao === "number") {
          versaoRef.current = initResp.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();

            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;

            try {
              resp = await awaitChange(config, cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(jitter(ERROR_BACKOFF_MS));
              }
            } finally {
              if (awaitCtrlRef.current === ac) {
                awaitCtrlRef.current = null;
              }

              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }

              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);

            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("usePerdasGenerico loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.key,
    config.areaSlug,
    config.estoqueSlug,
    config.departamento,
    JSON.stringify(query),
  ]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    rowMeta,
    loading,
    refresh,
    meta,
    erro,
  };
}

/** ---------------------- Modal styles ---------------------- */

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1120,
  background: "#ffffff",
  borderRadius: 0,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  border: "1px solid #0c2b53",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #0c2b53",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#111827",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 12px 0",
  fontSize: 14,
  fontWeight: 600,
  color: "#0c2b53",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 12,
  marginBottom: 20,
};

const fieldBoxStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  background: "#ffffff",
};

const fieldLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#0c2b53",
  marginBottom: 4,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#111827",
  fontWeight: 500,
  wordBreak: "break-word",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #0c2b53",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  background: "#ffffff",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#ffffff",
  color: "#111827",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonPrimaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#0c2b53",
  color: "#ffffff",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

/** ---------------------- Página ---------------------- */

const SaidasPerdasGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const headers = useMemo(
    () => [
      "Responsável",
      "Código",
      "Tipo",
      "Nome do Produto",
      "Custo Unitário",
      "Quantidade",
      "Motivo",
      "Data",
      "Hora",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "140px",
      "70px",
      "80px",
      "270px",
      "100px",
      "60px",
      "150px",
      "80px",
      "60px",
    ],
    []
  );

  const { rows, rowMeta, loading, erro } = usePerdasGenerico(config);

  const [modal, setModal] = useState<ModalState>({
    open: false,
    responsavel: "",
    motivo: "",
    data: "",
    hora: "",
    itens: [],
  });

  const enabledColumnIndexes = useMemo(
    () => headers.map((_header, index) => index),
    [headers]
  );

  const totalQuantidade = useMemo(() => {
    return rows.reduce((acc, row) => acc + parseQuantidade(row[5]), 0);
  }, [rows]);

  const valorTotal = useMemo(() => {
    return rows.reduce((acc, row) => {
      const custo = parseBRLValue(row[4]);
      const quantidade = parseQuantidade(row[5]);
      return acc + custo * quantidade;
    }, 0);
  }, [rows]);

  const gerarRelatorioPerdas = useCallback(() => {
    generatePdfReport({
      reportTitle: config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações da Saída de Materiais como Perdas",
      headers,
      rows,
      custoColIndex: 4,
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        `Total de Itens em Perda: ${totalQuantidade}`,
        `Valor Total das Perdas: R$ ${valorTotal.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      ],
    });
  }, [
    config.reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    enabledColumnIndexes,
    totalQuantidade,
    valorTotal,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const base = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/saidas/perdas`
      );

      const isPerdasContext =
        currentPath.includes(base) ||
        eventPath.includes(base) ||
        currentPath.includes("perdas") ||
        eventPath.includes("perdas") ||
        moduloLabel.includes("perdas") ||
        moduloCodigo.includes("perdas") ||
        moduloSlug.includes("perdas");

      if (!isPerdasContext) return;

      gerarRelatorioPerdas();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioPerdas]);

  const openModalPorLinha = (rowBase: Row, indexBase: number) => {
    const responsavel = String(rowBase[0] ?? "-");
    const motivo = String(rowBase[6] ?? "-");
    const data = String(rowBase[7] ?? "-");
    const hora = String(rowBase[8] ?? "-");

    setModal({
      open: true,
      responsavel,
      motivo,
      data,
      hora,
      itens: [
        {
          originalIndex: indexBase,
          row: rowBase,
          meta: rowMeta[indexBase] ?? null,
        },
      ],
    });
  };

  const closeModal = () => {
    setModal({
      open: false,
      responsavel: "",
      motivo: "",
      data: "",
      hora: "",
      itens: [],
    });
  };

  const modalTotals = useMemo(() => {
    let totalItens = 0;
    let totalQuantidadeModal = 0;
    let totalCusto = 0;

    for (const item of modal.itens) {
      const quantidade = parseQuantidade(item.row[5]);
      const custo = parseBRLValue(item.row[4]);

      totalItens += 1;
      totalQuantidadeModal += quantidade;
      totalCusto += quantidade * custo;
    }

    return {
      totalItens,
      totalQuantidade: totalQuantidadeModal,
      totalCusto,
    };
  }, [modal.itens]);

  const gerarRelatorioDoModal = () => {
    if (!modal.itens.length) return;

    const modalRows = modal.itens.map((item) => item.row);

    generatePdfReport({
      reportTitle: "Relatório de Perda",
      fileNamePrefix: `Perda-${modal.responsavel}-${modal.data}`.replace(
        /[^\w\-]+/g,
        "_"
      ),
      subtitle: `Perda de materiais - Responsável: ${modal.responsavel} | Motivo: ${modal.motivo} | Data: ${modal.data} ${modal.hora}`,
      headers,
      rows: modalRows,
      custoColIndex: 4,
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        `Itens na perda: ${modalTotals.totalItens}`,
        `Quantidade total: ${modalTotals.totalQuantidade}`,
        `Custo total estimado: ${modalTotals.totalCusto.toLocaleString(
          "pt-BR",
          {
            style: "currency",
            currency: "BRL",
          }
        )}`,
      ],
    });
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={config.title} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando perdas…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          onDoubleClick={(viewIndex, rowData, originalIndex) => {
            const idx = originalIndex ?? viewIndex;
            const row = (rows[idx] ?? rowData) as Row;

            if (!row) return;

            openModalPorLinha(row, idx);
          }}
        />
      )}

      {modal.open && (
        <div style={overlayStyle} onClick={closeModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>Detalhes da perda</h3>

                <div style={{ marginTop: 6, color: "#374151", fontSize: 14 }}>
                  <strong>{modal.responsavel}</strong> — {modal.motivo}
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              <h4 style={sectionTitleStyle}>Resumo da perda</h4>

              <div style={gridStyle}>
                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Responsável</span>
                  <div style={fieldValueStyle}>{modal.responsavel || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Motivo</span>
                  <div style={fieldValueStyle}>{modal.motivo || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Data / Hora</span>
                  <div style={fieldValueStyle}>
                    {modal.data || "-"} {modal.hora || ""}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Itens</span>
                  <div style={fieldValueStyle}>{modalTotals.totalItens}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Quantidade total</span>
                  <div style={fieldValueStyle}>
                    {modalTotals.totalQuantidade}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Custo total estimado</span>
                  <div style={fieldValueStyle}>
                    {modalTotals.totalCusto.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </div>
                </div>
              </div>

              <h4 style={sectionTitleStyle}>Materiais</h4>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr>
                      {headers.map((header) => (
                        <th
                          key={header}
                          style={{
                            border: "1px solid #0c2b53",
                            padding: 8,
                            background: "#f8fafc",
                            color: "#0c2b53",
                            textAlign: "left",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {modal.itens.map((item) => (
                      <tr key={item.originalIndex}>
                        {headers.map((_header, index) => (
                          <td
                            key={index}
                            style={{
                              border: "1px solid #0c2b53",
                              padding: 8,
                              color: "#111827",
                            }}
                          >
                            {item.row[index] ?? "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={modalFooterStyle}>
              <button
                type="button"
                onClick={closeModal}
                style={buttonSecondaryStyle}
              >
                Fechar
              </button>

              <button
                type="button"
                onClick={gerarRelatorioDoModal}
                style={buttonPrimaryStyle}
              >
                Gerar Relatório da Perda
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaidasPerdasGenerico;
